import type {
  PropertyHotel,
  HotelsByContinent,
  HotelsByContinentAndCity,
  HotelsByCity,
} from "@citizenm/lobby/shared/types/propertyHotel";

export default function useHotels() {
  const hotels = useState<PropertyHotel[]>("hotels", () => []);

  const { isUsCaUser } = useGeoIpLocation();

  const hotelsByCity = computed(() => {
    if (!hotels.value.length) return {};

    return hotels.value.reduce<HotelsByCity>((acc, { city, ...rest }) => {
      if (!acc[city.code]) {
        acc[city.code] = [];
      }
      acc[city.code].push({ city, ...rest });
      return acc;
    }, {});
  });

  const hotelsByContinent = computed(() => {
    if (!hotels.value.length) return {};

    return hotels.value.reduce<HotelsByContinent>(
      (acc, { continent, ...rest }) => {
        if (!continent) {
          return acc;
        }
        if (!acc[continent.code]) {
          acc[continent.code] = [];
        }
        acc[continent.code].push({
          continent,
          ...rest,
        });
        return acc;
      },
      /**
       * Initialize continents based on the user's location to prevent sorting action later.
       */
      {
        [isUsCaUser.value ? "AMERICA" : "EUROPE"]: [],
        [isUsCaUser.value ? "EUROPE" : "AMERICA"]: [],
        ASIA: [],
      },
    );
  });

  const hotelsByContinentAndCity = computed(() => {
    if (!hotels.value.length) return {};

    const hotelsByContinentAndCityObject =
      hotels.value.reduce<HotelsByContinentAndCity>(
        (acc, { city, continent, ...rest }) => {
          if (!continent || !city) return acc;

          if (!acc[continent.code]?.slug && !acc[continent.code]?.title) {
            acc[continent.code] = {
              slug: continent.slug,
              title: continent.title,
              cities: {},
            };
          }

          if (!acc[continent.code].cities[city.code]) {
            acc[continent.code].cities[city.code] = {
              slug: city.slug,
              title: city.title,
              // @ts-expect-error heroImage is expected to match the Contentful response, but this is coming from Goodbits. Typing properly is too time consuming right now.
              heroImage: city.heroImage,
              hotels: [],
            };
          }

          acc[continent.code].cities[city.code].hotels.push({
            city,
            continent,
            ...rest,
          });

          return acc;
        },
        /**
         * Initialize continents based on the user's location to prevent sorting action later.
         */
        {
          [isUsCaUser.value ? "AMERICA" : "EUROPE"]: {},
          [isUsCaUser.value ? "EUROPE" : "AMERICA"]: {},
          ASIA: {},
        },
      );

    /**
     * Sorting is done last to prevent having to sort in each iteration of the reducer.
     */
    for (const continent of Object.values(hotelsByContinentAndCityObject)) {
      continent.cities = Object.fromEntries(
        Object.entries(continent.cities).sort(([, a], [, b]) =>
          a.title.localeCompare(b.title),
        ),
      );
    }

    return hotelsByContinentAndCityObject;
  });

  function setHotels(newHotels: MaybeRef<PropertyHotel[] | null>) {
    hotels.value = unref(newHotels) || [];
  }

  return {
    hotels: readonly(hotels),
    hotelsByContinent,
    hotelsByContinentAndCity,
    hotelsByCity,
    setHotels,
  };
}
